import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useEffect, useMemo, useCallback } from 'react';
import { useGetQrWhatsappWithPolling } from '../../../api/get-qr-whatsapp.api';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY } from '../../../api/get-list-integration-whatsapp.api';
import { useNavigate } from 'react-router';
import { Loader2, TriangleAlert } from 'lucide-react';
import Banner from '@/components/ui/banner';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import { Modal, ModalHeader, ModalTitle, ModalDescription, ModalContent } from '@/components/_ui/modal';

export type TGetQrWhatsappModalType = 'GetQrWhatsappModal';
export interface IGetQrWhatsappModalProps {
  organizationChannelId: string;
  isPreparingQrCode?: boolean;
}

const GetQrWhatsappModal = memo(({ organizationChannelId, isPreparingQrCode = false }: IGetQrWhatsappModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const navigate = useNavigate();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenGetQrWhatsappModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('GetQrWhatsappModal');
  }, [channelModalRootActions]);

  const {
    data: dataQrWhatsapp,
    isLoading: isLoadingGetQrWhatsappData,
    isPending: isPendingGetQrWhatsappData,
    isError: isErrorGetQrWhatsapp,
  } = useGetQrWhatsappWithPolling({
    params: {
      organizationChannelId,
    },
    queryConfig: {
      enabled: !!organizationChannelId && isOpenGetQrWhatsappModal,
    },
  });

  const isReadyConnect = useMemo(() => {
    return dataQrWhatsapp?.payload?.isReady;
  }, [dataQrWhatsapp?.payload?.isReady]);

  const qrWhatsappBase64 = useMemo(() => {
    return dataQrWhatsapp?.payload?.qrCode;
  }, [dataQrWhatsapp?.payload?.qrCode]);

  const initMessage = useMemo(() => {
    return dataQrWhatsapp?.payload?.initMessage;
  }, [dataQrWhatsapp?.payload?.initMessage]);

  const isAuthenticated = useMemo(() => {
    return dataQrWhatsapp?.payload?.isAuthenticated;
  }, [dataQrWhatsapp?.payload?.isAuthenticated]);

  const handleCloseModal = useCallback(() => {
    channelModalRootActions.closeModal('GetQrWhatsappModal');
  }, [channelModalRootActions]);

  // EFFECTS
  useEffect(() => {
    if (isReadyConnect && isAuthenticated && isOpenGetQrWhatsappModal && !isErrorGetQrWhatsapp) {
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY],
      });
      channelModalRootActions.closeModal('GetQrWhatsappModal');
      navigate('/channel-integrations/chat-platform?tabActive=whatsapp');
      showToast({
        type: 'success',
        message: 'Whatsapp channel successfully connected!',
      });
    }
  }, [
    isReadyConnect,
    isAuthenticated,
    isOpenGetQrWhatsappModal,
    channelModalRootActions,
    showToast,
    navigate,
    isErrorGetQrWhatsapp,
  ]);

  // RETURN COMPONENT
  return (
    <Modal
      isOpen={isOpenGetQrWhatsappModal}
      lockBodyScroll
      onClose={handleCloseModal}
      isCloseOutside={false}
      className="w-[500px] p-[48px]"
    >
      <ModalHeader>
        <ModalTitle>Scan QR Code to Connect</ModalTitle>
        <ModalDescription className="text-[14px] text-[#666666] !mt-[16px]">
          Scan the QR code to link your WhatsApp account.
        </ModalDescription>
      </ModalHeader>
      <ModalContent>
        <Banner
          className="my-[8px]"
          icon={TriangleAlert}
          title="After scanning, the integration process will take about 1–2 minutes, and this pop-up will close automatically."
          variant="warning"
        />
        <div className="flex justify-center my-[18px]">
          {!isReadyConnect &&
          (isPreparingQrCode || isPendingGetQrWhatsappData || isLoadingGetQrWhatsappData) &&
          !qrWhatsappBase64 ? (
            <div className="flex items-center justify-center w-[200px] h-[200px]">
              <Loader2 className="animate-spin size-4 text-[#0C8DBF]" />
              <span className="ml-2 text-[14px] font-normal text-[#666666]">Loading QR code...</span>
            </div>
          ) : !isReadyConnect && !isAuthenticated && qrWhatsappBase64 ? (
            <div className="flex items-center justify-center w-[200px] h-[200px]">
              <img src={qrWhatsappBase64} alt="whatsapp-qr" className="shrink-0 rounded-[4px]" />
            </div>
          ) : !isReadyConnect && isAuthenticated && initMessage ? (
            <div className="flex items-center justify-center w-fit h-[200px]">
              <span className="text-[14px] font-normal text-[#666666]">{initMessage}</span>
            </div>
          ) : isReadyConnect && isAuthenticated ? (
            <div className="flex items-center justify-center w-fit h-[200px]">
              <span className="text-[14px] font-normal text-[#666666]">{initMessage}</span>
            </div>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
  );
});

GetQrWhatsappModal.displayName = 'GetQrWhatsappModal';

export default GetQrWhatsappModal;
